<template>
  <div class="fichaje-page">
    <!-- Header -->
    <div class="fichaje-header">
      <div>
        <span class="header-eyebrow">Control horario</span>
        <h2 class="header-title">Mi Fichaje</h2>
      </div>
      <div class="header-date">
        <i class="fa-regular fa-calendar"></i>
        {{ fechaHoy }}
      </div>
    </div>

    <!-- Status Hero -->
    <div class="status-hero" :class="fichado ? 'status-hero--active' : 'status-hero--idle'">
      <div class="status-hero__left">
        <div class="status-indicator" :class="fichado ? 'status-indicator--on' : 'status-indicator--off'">
          <span class="status-dot"></span>
        </div>
        <div class="status-info">
          <span class="status-label">{{ fichado ? 'Trabajando' : 'Fuera de turno' }}</span>
          <span class="status-time" v-if="fichado && horaEntrada">
            Entrada a las {{ formatHora(horaEntrada) }}
          </span>
          <span class="status-time" v-else>Sin fichaje activo</span>
        </div>
      </div>

      <div class="status-hero__center" v-if="fichado">
        <span class="timer-label">Tiempo trabajado</span>
        <span class="timer-value">{{ tiempoTrabajado }}</span>
      </div>

      <div class="status-hero__right">
        <button
          v-if="!fichado"
          class="btn-clock btn-clock--in"
          @click="clockIn"
          :disabled="loading"
        >
          <i class="fa-solid fa-right-to-bracket"></i>
          <span>Clock In</span>
        </button>
        <button
          v-else
          class="btn-clock btn-clock--out"
          @click="clockOut"
          :disabled="loading"
        >
          <i class="fa-solid fa-right-from-bracket"></i>
          <span>Clock Out</span>
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Fichajes</span>
        <span class="kpi-value">{{ historial.length }}</span>
        <span class="kpi-foot">en el periodo</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Horas totales</span>
        <span class="kpi-value">{{ totalHorasPeriodoNum.horas }}h {{ totalHorasPeriodoNum.minutos }}m</span>
        <span class="kpi-foot">acumuladas</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Media diaria</span>
        <span class="kpi-value">{{ mediaDiaria }}</span>
        <span class="kpi-foot">horas/dia</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Abiertos</span>
        <span class="kpi-value" :class="fichajesAbiertos > 0 ? 'kpi-value--warning' : ''">{{ fichajesAbiertos }}</span>
        <span class="kpi-foot">sin salida</span>
      </div>
    </div>

    <!-- Historial -->
    <div class="historial-card">
      <div class="historial-header">
        <h3 class="historial-title">Historial de fichajes</h3>
        <div class="historial-actions">
          <b-button variant="primary" size="sm" @click="abrirCrearFichaje">
            <i class="fa-solid fa-plus mr-50"></i> Nuevo
          </b-button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="historial-filters">
        <div class="filter-group">
          <label class="filter-label">Desde</label>
          <b-form-input
            type="date"
            v-model="filtroDesde"
            @change="cargarHistorial"
            size="sm"
            class="filter-input"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">Hasta</label>
          <b-form-input
            type="date"
            v-model="filtroHasta"
            @change="cargarHistorial"
            size="sm"
            class="filter-input"
          />
        </div>
        <div class="filter-group filter-group--quick">
          <button class="quick-filter" :class="{ active: isFilterHoy }" @click="setFiltroHoy">Hoy</button>
          <button class="quick-filter" :class="{ active: isFilterSemana }" @click="setFiltroSemana">Semana</button>
          <button class="quick-filter" :class="{ active: isFilterMes }" @click="setFiltroMes">Mes</button>
        </div>
      </div>

      <!-- Tabla -->
      <div class="table-wrapper">
        <b-table
          :items="historial"
          :fields="columnas"
          responsive
          hover
          show-empty
          empty-text="No hay fichajes en este periodo"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(fecha)="data">
            <span class="cell-fecha">{{ formatFecha(data.item.hora_entrada) }}</span>
          </template>

          <template #cell(hora_entrada)="data">
            <span class="cell-hora">{{ formatHora(data.item.hora_entrada) }}</span>
          </template>

          <template #cell(hora_salida)="data">
            <span v-if="data.item.hora_salida" class="cell-hora">
              {{ formatHora(data.item.hora_salida) }}
            </span>
            <span v-else class="badge-open">
              <span class="badge-open__dot"></span> Abierto
            </span>
          </template>

          <template #cell(total)="data">
            <span class="cell-total" v-if="data.item.hora_salida">{{ calcularTotalHoras(data.item) }}</span>
            <span class="cell-total cell-total--muted" v-else>--</span>
          </template>

          <template #cell(acciones)="data">
            <div class="cell-actions">
              <button
                class="action-btn action-btn--edit"
                @click="abrirEditarFichaje(data.item)"
                title="Editar fichaje"
              >
                <i class="fa-solid fa-pen"></i>
              </button>
              <button
                class="action-btn action-btn--delete"
                @click="abrirEliminarFichaje(data.item)"
                title="Eliminar fichaje"
              >
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Footer -->
      <div class="historial-footer">
        <span class="footer-total">
          Total periodo: <strong>{{ totalHorasPeriodoNum.horas }}h {{ totalHorasPeriodoNum.minutos }}m</strong>
        </span>
      </div>
    </div>

    <!-- Modal Crear Fichaje -->
    <b-modal
      id="modal-crear-fichaje"
      modal-class="modal-fichaje"
      hide-header
      hide-footer
      centered
      size="sm"
    >
      <div class="modal-custom">
        <div class="modal-custom__header">
          <div class="modal-custom__icon modal-custom__icon--primary">
            <i class="fa-solid fa-plus"></i>
          </div>
          <h4 class="modal-custom__title">Nuevo fichaje</h4>
          <p class="modal-custom__subtitle">Crea un registro manual de fichaje</p>
        </div>

        <div class="modal-custom__body">
          <div class="field">
            <label class="field__label">Fecha</label>
            <b-form-input type="date" v-model="nuevoForm.fecha" class="field__input" />
          </div>

          <div class="field-row">
            <div class="field">
              <label class="field__label">Entrada</label>
              <b-form-input type="time" v-model="nuevoForm.hora_entrada" class="field__input" />
            </div>
            <div class="field">
              <label class="field__label">Salida</label>
              <b-form-input type="time" v-model="nuevoForm.hora_salida" class="field__input" />
            </div>
          </div>

          <div class="field">
            <label class="field__label">Motivo <span class="field__required">*</span></label>
            <b-form-textarea
              v-model="nuevoForm.motivo"
              placeholder="Motivo de la creacion manual..."
              rows="2"
              class="field__input"
              :class="{ 'field__input--error': nuevoForm.motivo && nuevoForm.motivo.trim().length < 5 }"
            />
            <span class="field__hint" v-if="nuevoForm.motivo && nuevoForm.motivo.trim().length < 5">
              Minimo 5 caracteres
            </span>
          </div>
        </div>

        <div class="modal-custom__footer">
          <button class="btn-modal btn-modal--ghost" @click="$bvModal.hide('modal-crear-fichaje')">Cancelar</button>
          <button
            class="btn-modal btn-modal--primary"
            @click="guardarNuevoFichaje({ preventDefault: function(){} })"
            :disabled="!nuevoForm.fecha || !nuevoForm.hora_entrada || !nuevoForm.motivo || nuevoForm.motivo.trim().length < 5"
          >
            <i class="fa-solid fa-plus"></i> Crear fichaje
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Eliminar Fichaje -->
    <b-modal
      id="modal-eliminar-fichaje"
      modal-class="modal-fichaje"
      hide-header
      hide-footer
      centered
      size="sm"
    >
      <div class="modal-custom">
        <div class="modal-custom__header">
          <div class="modal-custom__icon modal-custom__icon--danger">
            <i class="fa-solid fa-trash"></i>
          </div>
          <h4 class="modal-custom__title">Eliminar fichaje</h4>
          <p class="modal-custom__subtitle">Esta accion no se puede deshacer</p>
        </div>

        <div class="modal-custom__body" v-if="fichajeEliminar">
          <div class="delete-detail">
            <div class="delete-detail__row">
              <span class="delete-detail__label">Fecha</span>
              <span class="delete-detail__value">{{ formatFecha(fichajeEliminar.hora_entrada) }}</span>
            </div>
            <div class="delete-detail__row">
              <span class="delete-detail__label">Entrada</span>
              <span class="delete-detail__value delete-detail__value--mono">{{ formatHora(fichajeEliminar.hora_entrada) }}</span>
            </div>
            <div class="delete-detail__row" v-if="fichajeEliminar.hora_salida">
              <span class="delete-detail__label">Salida</span>
              <span class="delete-detail__value delete-detail__value--mono">{{ formatHora(fichajeEliminar.hora_salida) }}</span>
            </div>
          </div>
        </div>

        <div class="modal-custom__footer">
          <button class="btn-modal btn-modal--ghost" @click="$bvModal.hide('modal-eliminar-fichaje')">Cancelar</button>
          <button class="btn-modal btn-modal--danger" @click="confirmarEliminarFichaje">
            <i class="fa-solid fa-trash"></i> Eliminar
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Editar Fichaje -->
    <b-modal
      id="modal-editar-fichaje"
      modal-class="modal-fichaje"
      hide-header
      hide-footer
      centered
      size="sm"
    >
      <div class="modal-custom">
        <div class="modal-custom__header">
          <div class="modal-custom__icon modal-custom__icon--primary">
            <i class="fa-solid fa-pen"></i>
          </div>
          <h4 class="modal-custom__title">Editar fichaje</h4>
          <p class="modal-custom__subtitle">{{ formatFecha(editForm.fecha) }}</p>
        </div>

        <div class="modal-custom__body">
          <div class="field-row">
            <div class="field">
              <label class="field__label">Entrada</label>
              <b-form-input type="time" v-model="editForm.hora_entrada" class="field__input" />
            </div>
            <div class="field">
              <label class="field__label">Salida</label>
              <div class="field__with-action">
                <b-form-input type="time" v-model="editForm.hora_salida" class="field__input" />
                <button
                  v-if="editForm.hora_salida"
                  class="field__clear"
                  @click="editForm.hora_salida = ''"
                  title="Vaciar"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="field__label">Motivo del cambio <span class="field__required">*</span></label>
            <b-form-textarea
              v-model="editForm.motivo"
              placeholder="Motivo de la modificacion..."
              rows="2"
              class="field__input"
              :class="{ 'field__input--error': editForm.motivo && editForm.motivo.trim().length < 5 }"
            />
            <span class="field__hint" v-if="editForm.motivo && editForm.motivo.trim().length < 5">
              Minimo 5 caracteres
            </span>
          </div>
        </div>

        <div class="modal-custom__footer">
          <button class="btn-modal btn-modal--ghost" @click="$bvModal.hide('modal-editar-fichaje')">Cancelar</button>
          <button
            class="btn-modal btn-modal--primary"
            @click="guardarEdicion({ preventDefault: function(){} })"
            :disabled="!editForm.motivo || editForm.motivo.trim().length < 5"
          >
            <i class="fa-solid fa-check"></i> Guardar cambios
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import { EventBus } from '@/libs/eventBus'

export default {
  name: 'Fichaje',
  data() {
    return {
      fichado: false,
      horaEntrada: null,
      fichajeActual: null,
      loading: false,
      historial: [],
      filtroDesde: '',
      filtroHasta: '',
      tiempoTrabajadoInterval: null,
      tiempoTrabajado: '00:00:00',
      columnas: [
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'hora_entrada', label: 'Entrada', sortable: true },
        { key: 'hora_salida', label: 'Salida', sortable: true },
        { key: 'total', label: 'Total' },
        { key: 'acciones', label: '', thStyle: { width: '100px' } },
      ],
      editForm: {
        id: null,
        fecha: null,
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      },
      fichajeEliminar: null,
      nuevoForm: {
        fecha: '',
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      },
    }
  },
  computed: {
    fechaHoy() {
      return new Date().toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },
    totalHorasPeriodoNum() {
      let totalMinutos = 0
      this.historial.forEach(item => {
        if (item.hora_entrada && item.hora_salida) {
          const [hE, mE] = this.formatHora(item.hora_entrada).split(':').map(Number)
          const [hS, mS] = this.formatHora(item.hora_salida).split(':').map(Number)
          const diff = (hS * 60 + mS) - (hE * 60 + mE)
          totalMinutos += diff
        }
      })
      return {
        horas: Math.floor(totalMinutos / 60),
        minutos: Math.round(totalMinutos % 60)
      }
    },
    mediaDiaria() {
      const diasUnicos = new Set()
      this.historial.forEach(item => {
        if (item.hora_entrada) {
          diasUnicos.add(String(item.hora_entrada).split(/[T ]/)[0])
        }
      })
      const numDias = diasUnicos.size || 1
      const totalMin = this.totalHorasPeriodoNum.horas * 60 + this.totalHorasPeriodoNum.minutos
      const media = totalMin / numDias
      const h = Math.floor(media / 60)
      const m = Math.round(media % 60)
      return `${h}h ${m}m`
    },
    fichajesAbiertos() {
      return this.historial.filter(item => !item.hora_salida).length
    },
    isFilterHoy() {
      const hoy = new Date().toISOString().split('T')[0]
      return this.filtroDesde === hoy && this.filtroHasta === hoy
    },
    isFilterSemana() {
      const hoy = new Date()
      const lunes = new Date(hoy)
      lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7))
      const domingo = new Date(lunes)
      domingo.setDate(lunes.getDate() + 6)
      return this.filtroDesde === lunes.toISOString().split('T')[0] && this.filtroHasta === domingo.toISOString().split('T')[0]
    },
    isFilterMes() {
      const hoy = new Date()
      const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
      return this.filtroDesde === primerDia.toISOString().split('T')[0] && this.filtroHasta === ultimoDia.toISOString().split('T')[0]
    }
  },
  methods: {
    async cargarEstado() {
      try {
        const response = await axios.get('/fichajes/estado')
        this.fichado = response.data.fichado
        this.horaEntrada = response.data.hora_entrada
        this.fichajeActual = response.data.fichaje
        if (this.fichado) {
          this.iniciarContador()
        }
      } catch (error) {
        console.error('Error al cargar estado:', error)
      }
    },

    async clockIn() {
      this.loading = true
      try {
        const response = await axios.post('/fichajes/entrada')
        this.fichado = true
        this.horaEntrada = response.data.hora_entrada
        this.fichajeActual = response.data.fichaje

        this.$bvToast.toast('Clock In registrado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        EventBus.$emit('fichaje-actualizado')
        this.iniciarContador()
        this.cargarHistorial()
      } catch (error) {
        console.error('Error en clock in:', error)
        this.$bvToast.toast(error.response && error.response.data && error.response.data.message || 'Error al registrar entrada', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    async clockOut() {
      this.loading = true
      try {
        await axios.post('/fichajes/salida')
        this.fichado = false
        this.horaEntrada = null
        this.fichajeActual = null

        this.$bvToast.toast('Clock Out registrado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        EventBus.$emit('fichaje-actualizado')
        this.detenerContador()
        this.cargarHistorial()
      } catch (error) {
        console.error('Error en clock out:', error)
        this.$bvToast.toast(error.response && error.response.data && error.response.data.message || 'Error al registrar salida', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    async cargarHistorial() {
      try {
        const params = {}
        if (this.filtroDesde) params.desde = this.filtroDesde
        if (this.filtroHasta) params.hasta = this.filtroHasta

        const response = await axios.get('/fichajes/historial', { params })
        this.historial = response.data
      } catch (error) {
        console.error('Error al cargar historial:', error)
      }
    },

    iniciarContador() {
      this.actualizarTiempoTrabajado()
      this.tiempoTrabajadoInterval = setInterval(() => {
        this.actualizarTiempoTrabajado()
      }, 1000)
    },

    detenerContador() {
      if (this.tiempoTrabajadoInterval) {
        clearInterval(this.tiempoTrabajadoInterval)
        this.tiempoTrabajadoInterval = null
      }
      this.tiempoTrabajado = '00:00:00'
    },

    actualizarTiempoTrabajado() {
      if (!this.horaEntrada) return
      const horaStr = this.formatHora(this.horaEntrada)
      const [horaEntrada, minEntrada] = horaStr.split(':').map(Number)

      const ahora = new Date()
      const horaActual = ahora.getHours()
      const minActual = ahora.getMinutes()
      const segActual = ahora.getSeconds()

      let diffSegundos = (horaActual * 3600 + minActual * 60 + segActual) - (horaEntrada * 3600 + minEntrada * 60)
      diffSegundos = Math.max(0, diffSegundos)

      const horas = Math.floor(diffSegundos / 3600)
      const minutos = Math.floor((diffSegundos % 3600) / 60)
      const segundos = diffSegundos % 60

      this.tiempoTrabajado = `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`
    },

    formatFecha(fecha) {
      if (!fecha) return ''
      const str = String(fecha).split(/[T ]/)[0]
      const [year, month, day] = str.split('-').map(Number)
      const d = new Date(year, month - 1, day)
      return d.toLocaleDateString('es-ES', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    formatHora(datetime) {
      if (!datetime) return ''
      const str = String(datetime)
      const match = str.match(/(\d{2}):(\d{2})/)
      if (match) {
        return `${match[1]}:${match[2]}`
      }
      return ''
    },

    calcularTotalHoras(item) {
      if (!item.hora_entrada || !item.hora_salida) return '-'
      const [hE, mE] = this.formatHora(item.hora_entrada).split(':').map(Number)
      const [hS, mS] = this.formatHora(item.hora_salida).split(':').map(Number)
      const diffMinutos = (hS * 60 + mS) - (hE * 60 + mE)
      const horas = Math.floor(diffMinutos / 60)
      const minutos = Math.round(diffMinutos % 60)
      return `${horas}h ${minutos}m`
    },

    setFiltroHoy() {
      const hoy = new Date().toISOString().split('T')[0]
      this.filtroDesde = hoy
      this.filtroHasta = hoy
      this.cargarHistorial()
    },

    setFiltroSemana() {
      const hoy = new Date()
      const lunes = new Date(hoy)
      lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7))
      const domingo = new Date(lunes)
      domingo.setDate(lunes.getDate() + 6)
      this.filtroDesde = lunes.toISOString().split('T')[0]
      this.filtroHasta = domingo.toISOString().split('T')[0]
      this.cargarHistorial()
    },

    setFiltroMes() {
      const hoy = new Date()
      const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
      this.filtroDesde = primerDia.toISOString().split('T')[0]
      this.filtroHasta = ultimoDia.toISOString().split('T')[0]
      this.cargarHistorial()
    },

    onFichajeActualizado() {
      this.cargarEstado()
      this.cargarHistorial()
    },

    abrirCrearFichaje() {
      this.nuevoForm = {
        fecha: new Date().toISOString().split('T')[0],
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      }
      this.$bvModal.show('modal-crear-fichaje')
    },

    async guardarNuevoFichaje(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.nuevoForm.fecha || !this.nuevoForm.hora_entrada) {
        this.$bvToast.toast('Completa todos los campos obligatorios', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      if (!this.nuevoForm.motivo || this.nuevoForm.motivo.trim().length < 5) {
        this.$bvToast.toast('El motivo es obligatorio (minimo 5 caracteres)', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        const fecha = this.nuevoForm.fecha
        const userData = JSON.parse(localStorage.getItem('userData'))

        await axios.post('/fichajes/crear', {
          usuario_id: userData.id,
          hora_entrada: `${fecha}T${this.nuevoForm.hora_entrada}:00`,
          hora_salida: this.nuevoForm.hora_salida ? `${fecha}T${this.nuevoForm.hora_salida}:00` : null,
          motivo: this.nuevoForm.motivo
        })

        this.$bvToast.toast('Fichaje creado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        this.$bvModal.hide('modal-crear-fichaje')
        this.cargarHistorial()
        EventBus.$emit('fichaje-actualizado')
      } catch (error) {
        console.error('Error al crear fichaje:', error)
        this.$bvToast.toast(error.response && error.response.data && error.response.data.message || 'Error al crear fichaje', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    abrirEditarFichaje(fichaje) {
      this.editForm.id = fichaje.id
      const entradaStr = String(fichaje.hora_entrada || '')
      const salidaStr = String(fichaje.hora_salida || '')

      this.editForm.fecha = entradaStr.split(/[T ]/)[0] || ''
      this.editForm.hora_entrada = this.formatHora(entradaStr)
      this.editForm.hora_salida = salidaStr ? this.formatHora(salidaStr) : ''
      this.editForm.motivo = ''
      this.$bvModal.show('modal-editar-fichaje')
    },

    async guardarEdicion(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.editForm.motivo || this.editForm.motivo.trim().length < 5) {
        this.$bvToast.toast('El motivo es obligatorio (minimo 5 caracteres)', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        const fecha = this.editForm.fecha

        await axios.put(`/fichajes/${this.editForm.id}`, {
          hora_entrada: this.editForm.hora_entrada ? `${fecha}T${this.editForm.hora_entrada}:00` : null,
          hora_salida: this.editForm.hora_salida ? `${fecha}T${this.editForm.hora_salida}:00` : null,
          motivo: this.editForm.motivo
        })

        this.$bvToast.toast('Fichaje actualizado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        this.$bvModal.hide('modal-editar-fichaje')
        this.cargarHistorial()
        EventBus.$emit('fichaje-actualizado')
      } catch (error) {
        console.error('Error al editar fichaje:', error)
        this.$bvToast.toast(error.response && error.response.data && error.response.data.message || 'Error al editar fichaje', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    abrirEliminarFichaje(fichaje) {
      this.fichajeEliminar = fichaje
      this.$bvModal.show('modal-eliminar-fichaje')
    },

    async confirmarEliminarFichaje() {
      try {
        await axios.delete(`/fichajes/${this.fichajeEliminar.id}`)
        this.$bvToast.toast('Fichaje eliminado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })
        this.fichajeEliminar = null
        this.cargarHistorial()
        EventBus.$emit('fichaje-actualizado')
      } catch (error) {
        console.error('Error al eliminar fichaje:', error)
        this.$bvToast.toast(error.response && error.response.data && error.response.data.message || 'Error al eliminar fichaje', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    }
  },
  mounted() {
    axios.post("/uso", { seccion: "fichaje" })
    this.setFiltroHoy()
    this.cargarEstado()
    this.cargarHistorial()
    EventBus.$on('fichaje-actualizado', this.onFichajeActualizado)
  },
  beforeDestroy() {
    this.detenerContador()
    EventBus.$off('fichaje-actualizado', this.onFichajeActualizado)
  }
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$success-light: #D1FAE5;
$danger: #EF4444;
$danger-light: #FEE2E2;
$warning: #F59E0B;
$warning-light: #FEF3C7;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
$shadow-md: 0 4px 6px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.03);

.fichaje-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 8px;
}

// Header
.fichaje-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-date {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
}

// Status Hero
.status-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-radius: $radius;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: $shadow;

  &--active {
    background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%);
    border: 1px solid #A7F3D0;
  }

  &--idle {
    background: $card-bg;
    border: 1px solid $border;
  }
}

.status-hero__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.status-indicator {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--on {
    background: rgba($success, 0.15);
  }

  &--off {
    background: rgba($text-muted, 0.1);
  }
}

.status-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: block;

  .status-indicator--on & {
    background: $success;
    box-shadow: 0 0 0 4px rgba($success, 0.2);
    animation: pulse 2s ease-in-out infinite;
  }

  .status-indicator--off & {
    background: #CBD5E1;
  }
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba($success, 0.2); }
  50% { box-shadow: 0 0 0 8px rgba($success, 0.1); }
}

.status-info {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: $text;
}

.status-time {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
  margin-top: 2px;
}

.status-hero__center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timer-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  margin-bottom: 2px;
}

.timer-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 32px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  letter-spacing: 2px;
}

// Clock Buttons
.btn-clock {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &--in {
    background: $success;
    color: white;
    box-shadow: 0 2px 8px rgba($success, 0.3);

    &:hover:not(:disabled) {
      background: darken($success, 5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($success, 0.4);
    }
  }

  &--out {
    background: $danger;
    color: white;
    box-shadow: 0 2px 8px rgba($danger, 0.3);

    &:hover:not(:disabled) {
      background: darken($danger, 5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($danger, 0.4);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 16px;
  }
}

// KPI Strip
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--warning {
    color: $warning;
  }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

// Historial Card
.historial-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;
}

.historial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px 0;
}

.historial-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

// Filters
.historial-filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 14px 22px;
  border-bottom: 1px solid $border;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  margin: 0;
}

.filter-input {
  width: 155px;
}

.filter-group--quick {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-left: auto;
  padding-bottom: 1px;
}

.quick-filter {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: white;
  }
}

// Table
.table-wrapper {
  padding: 0;
}

// Los ::v-deep van bajo .fichaje-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.fichaje-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 16px;
  background: transparent;
}

.fichaje-page ::v-deep .table-row td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 14px;
}

.fichaje-page ::v-deep .table-row:hover td {
  background: $bg;
}

.cell-fecha {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
  color: $text;
  text-transform: capitalize;
}

.cell-hora {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 500;
  color: $text;
  font-feature-settings: "tnum";
}

.cell-total {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  font-feature-settings: "tnum";

  &--muted {
    color: $text-muted;
  }
}

.badge-open {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  background: $warning-light;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  color: darken($warning, 10%);
}

.badge-open__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $warning;
}

.cell-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 12px;

  &--edit:hover {
    background: lighten($primary, 35%);
    color: $primary;
    border-color: lighten($primary, 25%);
  }

  &--delete:hover {
    background: $danger-light;
    color: $danger;
    border-color: lighten($danger, 20%);
  }
}

// Footer
.historial-footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 22px;
  border-top: 1px solid $border;
}

.footer-total {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  color: $text-muted;

  strong {
    color: $text;
    font-weight: 700;
  }
}

// Responsive
@media (max-width: 768px) {
  .status-hero {
    flex-direction: column;
    gap: 16px;
    padding: 18px;
    text-align: center;
  }

  .status-hero__left {
    justify-content: center;
  }

  .status-hero__right {
    width: 100%;

    .btn-clock {
      width: 100%;
      justify-content: center;
    }
  }

  .kpi-strip {
    grid-template-columns: repeat(2, 1fr);
  }

  .historial-filters {
    flex-wrap: wrap;
  }

  .filter-group--quick {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }

  .fichaje-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

</style>

<style lang="scss">
// Dark mode (unscoped to access .dark-layout ancestor)
.dark-layout {
  .fichaje-page {
    .header-title { color: #E2E8F0; }
    .header-date { color: #94A3B8; }

    .status-hero--active {
      background: linear-gradient(135deg, rgba(#10B981, 0.1) 0%, rgba(#10B981, 0.05) 100%);
      border-color: rgba(#10B981, 0.2);
    }

    .status-hero--idle {
      background: #1E293B;
      border-color: #334155;
    }

    .status-label { color: #E2E8F0; }
    .timer-value { color: #F1F5F9; }

    .kpi-card {
      background: #1E293B;
      border-color: #334155;
    }

    .kpi-value { color: #F1F5F9; }

    .historial-card {
      background: #1E293B;
      border-color: #334155;
    }

    .historial-title { color: #E2E8F0; }

    .historial-filters {
      border-color: #334155;
    }

    .quick-filter {
      border-color: #475569;
      color: #94A3B8;

      &:hover {
        border-color: #3B82F6;
        color: #3B82F6;
      }

      &.active {
        background: #3B82F6;
        border-color: #3B82F6;
        color: white;
      }
    }

    .table-head th {
      color: #94A3B8;
      border-color: #334155;
    }

    .table-row td {
      border-color: #334155;
      color: #E2E8F0;
    }

    .table-row:hover td {
      background: rgba(255,255,255,0.03);
    }

    .cell-fecha { color: #E2E8F0; }
    .cell-hora { color: #E2E8F0; }

    .historial-footer {
      border-color: #334155;
    }

    .footer-total {
      strong { color: #F1F5F9; }
    }

    .action-btn {
      color: #94A3B8;
    }
  }

  // Dark modal
  .modal-fichaje .modal-content {
    background: #1E293B;
    border-color: #334155;
  }

  .modal-custom__title { color: #E2E8F0; }
  .modal-custom__subtitle { color: #94A3B8; }
  .field__label { color: #94A3B8; }
  .delete-detail { background: rgba(255,255,255,0.03); border-color: #334155; }
  .delete-detail__label { color: #64748B; }
  .delete-detail__value { color: #E2E8F0; }

  .field__input,
  .modal-fichaje .form-control {
    background: #0F172A;
    border-color: #334155;
    color: #E2E8F0;

    &:focus {
      border-color: #3B82F6;
      background: #0F172A;
      color: #E2E8F0;
    }
  }

  .btn-modal--ghost {
    color: #94A3B8;
    &:hover { background: rgba(255,255,255,0.05); color: #E2E8F0; }
  }

  .field__clear {
    background: #334155;
    color: #94A3B8;
    &:hover { background: #475569; color: #E2E8F0; }
  }
}

// Modal styles (unscoped for portal rendering)
.modal-fichaje {
  .modal-content {
    border-radius: 16px;
    border: 1px solid #E2E8F0;
    box-shadow: 0 20px 60px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  .modal-body {
    padding: 0;
  }
}

.modal-custom {
  &__header {
    text-align: center;
    padding: 28px 28px 0;
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    font-size: 18px;

    &--primary {
      background: rgba(#3B82F6, 0.1);
      color: #3B82F6;
    }

    &--danger {
      background: rgba(#EF4444, 0.1);
      color: #EF4444;
    }
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #1E293B;
    margin: 0 0 4px;
  }

  &__subtitle {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #64748B;
    margin: 0;
    text-transform: capitalize;
  }

  &__body {
    padding: 22px 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0 28px 24px;
  }
}

// Fields
.field {
  display: flex;
  flex-direction: column;
  gap: 5px;

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #64748B;
    margin: 0;
  }

  &__required {
    color: #EF4444;
  }

  &__input {
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    padding: 8px 12px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    color: #1E293B;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #3B82F6;
      box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1);
    }

    &--error {
      border-color: #EF4444;

      &:focus {
        box-shadow: 0 0 0 3px rgba(#EF4444, 0.1);
      }
    }
  }

  &__hint {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #EF4444;
  }

  &__with-action {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__clear {
    position: absolute;
    right: 6px;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: none;
    background: #F1F5F9;
    color: #64748B;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: all 0.15s ease;

    &:hover {
      background: #E2E8F0;
      color: #1E293B;
    }
  }

  &__with-action .field__input {
    padding-right: 38px;
    width: 100%;
  }
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

// Delete detail
.delete-detail {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94A3B8;
  }

  &__value {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1E293B;
    text-transform: capitalize;

    &--mono {
      font-family: 'JetBrains Mono', monospace;
      font-feature-settings: "tnum";
    }
  }
}

// Modal buttons
.btn-modal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &--primary {
    background: #3B82F6;
    color: white;

    &:hover:not(:disabled) {
      background: #2563EB;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &--danger {
    background: #EF4444;
    color: white;

    &:hover {
      background: #DC2626;
    }
  }

  &--ghost {
    background: transparent;
    color: #64748B;

    &:hover {
      background: #F1F5F9;
      color: #1E293B;
    }
  }
}
</style>
